import { useState } from 'react';
import { Button, LinearProgress } from '@mui/material';
import { Add, Delete } from '@mui/icons-material/';
import { GridActionsCellItem, DataGrid, GridRowParams, GridRowId } from '@mui/x-data-grid';
import { red } from '@mui/material/colors';
import Popup from '../../../components/Popup';
import ConfirmDialog, { IConfirmDialog } from '../../../components/form/controls/ConfirmDialog';
import { LightToolTip } from '../../../components/form/react-hook-form-components/ToolTipTheme';
import useDataGridStyles from '../../../styles/useDataGridStyles';
import ManagerProxyQueries from './ManagerProxyQueries';
import ManagerProxyForm from './ManagerProxyForm';

export const ManagerProxyTable = () => {
  const { FetchManagerProxies, mutateDeleteManagerProxy, mutateCreateManagerProxy } = ManagerProxyQueries();
  const { data: proxies, isLoading: isLoadingProxies, isError, error } = FetchManagerProxies();
  const [IsCreateFormOpen, SetIsCreateFormOpen] = useState(false);
  const classes = useDataGridStyles();
  const [deleteDialog, setDeleteDialog] = useState({
    isOpen: false,
    title: '',
    content: '',
  } as IConfirmDialog);

  if (isLoadingProxies) return <LinearProgress />;
  if (isError) return <div>Error Loading Manager Proxies: {error}</div>;

  /**
   * Handles the deletion of a manager proxy id
   * @param id manager proxy id
   */
  const handleDeleteProxy = (id: GridRowId) => {
    setDeleteDialog({
      isOpen: true,
      title: 'Are you sure you want to delete this proxy?',
      content: "You can't undo this operation",
      onConfirm: () => {
        mutateDeleteManagerProxy(id as number, {
          onSuccess: closeDeleteDialog,
        });
      },
    } as IConfirmDialog);
  };

  /**
   * Closes the delete dialog
   */
  const closeDeleteDialog = () => {
    setDeleteDialog({
      ...deleteDialog,
      isOpen: false,
    });
  };

  /**
   * The Data Grid column headings
   */
  const columns = [
    {
      field: 'managerName',
      headerName: 'User',
      editable: false,
      flex: 1,
    },
    {
      field: 'proxyEmployeeName',
      headerName: 'Proxy User',
      editable: false,
      flex: 1,
    },
    {
      field: 'actions',
      headerName: 'Actions',
      type: 'actions',
      sortable: false,
      getActions: (params: GridRowParams) => {
        return [
          <GridActionsCellItem
            icon={
              <LightToolTip title="Delete" placement="bottom">
                <Delete sx={{ color: red[900] }} />
              </LightToolTip>
            }
            label="Delete"
            onClick={() => handleDeleteProxy(params.id)}
          />,
        ];
      },
    },
  ];

  return (
    <div style={{ margin: '1rem' }}>
      <Button startIcon={<Add />} size="medium" color="primary" onClick={() => SetIsCreateFormOpen(true)}>
        Create Proxy
      </Button>
      <Popup openPopup={IsCreateFormOpen} setOpenPopup={SetIsCreateFormOpen} title="Create Proxy">
        <ManagerProxyForm
          onCancelHandler={() => SetIsCreateFormOpen(false)}
          onSubmitHandler={values => {
            mutateCreateManagerProxy(values, {
              onSuccess: () => SetIsCreateFormOpen(false),
            });
          }}
        />
      </Popup>
      <ConfirmDialog confirmDialog={deleteDialog} setConfirmDialog={setDeleteDialog} />
      <DataGrid
        className={classes.root}
        autoHeight
        columns={columns}
        sx={{ display: 'flex', height: '100%', width: 'fitContent', maxWidth: 600 }}
        rows={proxies ?? []}
        getRowId={row => row.managerProxyId}
        disableSelectionOnClick={true}
      />
    </div>
  );
};
